/**
 * StandDurumuModal.jsx — tek bir standın durum özeti
 * Açan: TumStandlarListPage.jsx (satırdaki "Durum" butonu)
 *
 * Neden liste hücresi değil de popup: proje tarafı (Proje Beyanı aşaması, belge
 * onayları, revizyonlar, anlaşma/finans) roster'da YOK, `GET /projects/by-stand/{id}`
 * gerektiriyor. Bunu her satır için çekmek sayfa başına 25 istek demek — bu yüzden
 * yalnızca kullanıcı sorduğunda, o tek stand için çekiliyor.
 *
 * İzinler (İnşa/İş) ve taahhütnameler roster'dan GELİYOR — onlar için ek istek yok,
 * satırın kendisi `stand` prop'uyla veriliyor.
 *
 * Reset-on-open: parent bu bileşeni `key={standId}` ile yalnızca açıkken render eder;
 * her açılış taze state ile başlar, "açılınca sıfırla" Effect'i gerekmez.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge, Spinner, Alert, Row, Col,
} from "reactstrap";
import { getStandDurumu } from "../../../../services/standlarListService";
import { COMMITMENT_STATE } from "../../../../services/standOverviewService";
import ImzaSeviyesiRozeti from "../../../../Components/Projects/ImzaSeviyesiRozeti";
import { FILE_APPROVAL_STATUS, DEAL_STATUS, FINANCE_APPROVAL } from "../../../../services/projectService";
import { permitBadge, workPermitBadge, STATUS_HEX } from "../../../../Components/Projects/StandPills";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

// Rozet rengi listedeki durum kareleriyle AYNI hex paletten (StandPills.STATUS_HEX) —
// bu temanın Bootstrap renkleri kayık: `success` koyu gri, `secondary` teal basılıyor,
// yani "Borç: Yok" listede teal, popup'ta koyu gri görünüyordu. Palet dışı renkler
// ("secondary" vb. nötr durumlar) açık gri.
function NeutralAwareBadge({ badge }) {
  const hex = STATUS_HEX[badge.color];
  if (!hex) return <Badge color="light" className="text-muted border">{badge.label}</Badge>;
  return <span className="badge text-white" style={{ background: hex }}>{badge.label}</span>;
}

function StatusRow({ label, badge, note = null }) {
  return (
    <div className="d-flex align-items-center justify-content-between gap-2 py-2 border-bottom border-bottom-dashed">
      <span className="text-muted">{label}</span>
      <span className="text-end">
        {badge ? <NeutralAwareBadge badge={badge} /> : <span className="text-muted">—</span>}
        {note && <div className="text-muted fs-11">{note}</div>}
      </span>
    </div>
  );
}

// Proje Beyanı'nın hangi aşamada olduğu — beyanı guide §A adımlarıyla aynı sıra.
function beyanBadge(pb) {
  if (!pb) return null;
  if (!pb.declared) return { label: i18n.t("moduller:standDurumuModal.beyanBekleniyor"), color: "secondary" };
  if (pb.locked && !pb.overridden) return { label: i18n.t("moduller:standDurumuModal.kilitliKuralIhlali"), color: "danger" };
  if (pb.awaitingSystemAssignment) return { label: i18n.t("moduller:standDurumuModal.beyanEdildiOnayBekliyor"), color: "warning" };
  if (pb.systemAssigned) return { label: i18n.t("moduller:standDurumuModal.kabulEdildi"), color: "success" };
  return { label: i18n.t("moduller:standDurumuModal.beyanEdildi"), color: "info" };
}

export default function StandDurumuModal({ stand, onClose }) {
  const { t } = useTranslation("moduller");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;
    getStandDurumu(stand.standId)
      .then((d) => { if (!ignore) { setData(d); setLoading(false); } })
      .catch((e) => { if (!ignore) { setError(e?.message || i18n.t("moduller:standDurumuModal.durumAlinamadi")); setLoading(false); } });
    return () => { ignore = true; };
  }, [stand.standId]);

  const project = data?.project;
  // borcDurumu düşerse roster'ın legacy borç bayrağına düş.
  const hasDebt = data?.hasDebt ?? stand.hasDebt;
  const commitments = stand.commitments || [];

  const goDetail = () => navigate(`/Management/Standlar/${stand.standId}`);

  return (
    <Modal isOpen toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        <span className="me-2">{stand.standName}</span>
        <span className="text-muted fs-13 fw-normal">
          {stand.firmaShortName || stand.firmaName || t("standDurumuModal.katilimciAtanmamis")}
        </span>
      </ModalHeader>
      <ModalBody>
        <Row className="g-4">
          {/* ── Roster'dan (anında) ── */}
          <Col md={6}>
            <h6 className="text-uppercase text-muted fs-12 mb-1">{t("standDurumuModal.izinlerBorc")}</h6>
            <StatusRow label={t("standDurumuModal.insaIzni")} badge={permitBadge(stand)} />
            <StatusRow label={t("standDurumuModal.isIzni")} badge={workPermitBadge(stand)} />
            <StatusRow
              label={t("standDurumuModal.borc")}
              badge={hasDebt ? { label: t("standDurumuModal.var"), color: "danger" } : { label: t("standDurumuModal.yok"), color: "success" }}
            />
            {commitments.length > 0 && (
              <>
                <h6 className="text-uppercase text-muted fs-12 mt-3 mb-1">{t("standDurumuModal.taahhutnameler")}</h6>
                {commitments.map((c) => (
                  <StatusRow
                    key={c.key}
                    label={c.label}
                    badge={COMMITMENT_STATE[c.state] ?? null}
                    note={c.level || c.sourceName ? (
                      <>
                        {/* #506: Elektrik only - who signed. */}
                        <ImzaSeviyesiRozeti level={c.level} elektrikciName={c.elektrikciName} className="mb-1" />
                        {c.sourceName}
                      </>
                    ) : null}
                  />
                ))}
              </>
            )}
          </Col>

          {/* ── Proje kaydından (istekle) ── */}
          <Col md={6}>
            <h6 className="text-uppercase text-muted fs-12 mb-1">{t("standDurumuModal.proje")}</h6>
            {loading ? (
              <div className="text-center py-4"><Spinner size="sm" color="primary" /></div>
            ) : error ? (
              <Alert color="danger" className="py-2 mb-0">{error}</Alert>
            ) : !project ? (
              <div className="text-muted py-2">{t("standDurumuModal.buStandIcinProjeKaydi")}</div>
            ) : (
              <>
                <StatusRow label={t("standDurumuModal.projeBeyani")} badge={beyanBadge(project.projeBeyani)} />
                <StatusRow label={t("standDurumuModal.anlasma")} badge={DEAL_STATUS[project.dealStatus] ?? null} />
                <StatusRow label={t("standDurumuModal.finansOnayi")} badge={FINANCE_APPROVAL[project.financeApproval] ?? null} />
                <StatusRow
                  label={t("standDurumuModal.revizyonlar")}
                  badge={project.openRevisions > 0
                    ? { label: t("standDurumuModal.acik", { openRevisions: project.openRevisions }), color: "danger" }
                    : { label: t("standDurumuModal.yok_2"), color: "success" }}
                  note={project.awaitingUs > 0 ? t("standDurumuModal.tanesiKarariniziBekliyor", { awaitingUs: project.awaitingUs }) : null}
                />

                <h6 className="text-uppercase text-muted fs-12 mt-3 mb-1">{t("standDurumuModal.projeBelgeleri")}</h6>
                {project.documents.length === 0 ? (
                  <div className="text-muted py-2">{t("standDurumuModal.gerekliBelgeTanimliDegil")}</div>
                ) : project.documents.map((d) => (
                  <StatusRow
                    key={d.standFileId}
                    label={d.label}
                    // Hiç dosya yüklenmemişse "Beklemede" yanıltıcı — önce yükleme lazım.
                    badge={d.uploadedCount === 0
                      ? { label: t("standDurumuModal.yuklenmedi"), color: "secondary" }
                      : FILE_APPROVAL_STATUS[d.status] ?? FILE_APPROVAL_STATUS[0]}
                  />
                ))}
              </>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>{t("common:kapat")}</Button>
        <Button color="primary" onClick={goDetail}>
          <i className="bx bx-right-arrow-alt me-1" />{t("standDurumuModal.standDetayi")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
